import React, { useEffect, useRef, useState } from 'react';

// Import contexts and util modules
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import { isValidCurrencyForTransactionProcess } from '../../util/fieldHelpers.js';
import { propTypes } from '../../util/types';
import { ensureTransaction } from '../../util/data';
import { createSlug } from '../../util/urlHelpers';
import { isTransactionInitiateListingNotFoundError } from '../../util/errors';
import { getProcess, isBookingProcessAlias } from '../../transactions/transaction';

// Import shared components
import {
  Button,
  H3,
  H4,
  NamedLink,
  OrderBreakdown,
  Page,
  IconAlert,
  IconSpinner,
  Modal,
  SecondaryButton,
  PrimaryButton,
} from '../../components';

import {
  bookingDatesMaybe,
  getBillingDetails,
  getFormattedTotalPrice,
  getShippingDetailsMaybe,
  getTransactionTypeData,
  hasDefaultPaymentMethod,
  hasPaymentExpired,
  hasTransactionPassedPendingPayment,
  processCheckoutWithCustomPayment,
  processCheckoutWithPayment,
  processCustomCheckout,
  setOrderPageInitialValues,
} from './CheckoutPageTransactionHelpers.js';
import { getErrorMessages } from './ErrorMessages';

import CustomTopbar from './CustomTopbar';
// import StripePaymentForm from './StripePaymentForm/StripePaymentForm';
// import DetailsSideCard from './DetailsSideCard';
// import MobileListingImage from './MobileListingImage';
// import MobileOrderBreakdown from './MobileOrderBreakdown';

import css from './CheckoutPage.module.css';
import PaymentForm from './StripePaymentForm/PaymentForm.js';
import { types as sdkTypes } from '../../util/sdkLoader.js';
import { loggedInUserType } from '../../ducks/user.duck.js';
import CustomSlideCard from './CustomSlideCard.js';
import classNames from 'classnames';
import { apiBaseUrl } from '../../util/api.js';
import axios from 'axios';
import BackButton from '../../components/Button/BackButton.js';
import Loader from '../../components/Loader/Loader.js';
const { LatLng } = sdkTypes;

// Stripe PaymentIntent statuses, where user actions are already completed
// https://stripe.com/docs/payments/payment-intents/status
const STRIPE_PI_USER_ACTIONS_DONE_STATUSES = ['processing', 'requires_capture', 'succeeded'];

// Payment charge options
const ONETIME_PAYMENT = 'ONETIME_PAYMENT';
const PAY_AND_SAVE_FOR_LATER_USE = 'PAY_AND_SAVE_FOR_LATER_USE';
const USE_SAVED_CARD = 'USE_SAVED_CARD';
const listenerOrigin =
  process.env.REACT_APP_TRADESAFE_FRAME_ORIGIN || 'https://pay-sit.tradesafe.dev';

const paymentFlow = (selectedPaymentMethod, saveAfterOnetimePayment) => {
  // Payment mode could be 'replaceCard', but without explicit saveAfterOnetimePayment flag,
  // we'll handle it as one-time payment
  return selectedPaymentMethod === 'defaultCard'
    ? USE_SAVED_CARD
    : saveAfterOnetimePayment
    ? PAY_AND_SAVE_FOR_LATER_USE
    : ONETIME_PAYMENT;
};

const capitalizeString = s => `${s.charAt(0).toUpperCase()}${s.substr(1)}`;

/**
 * Prefix the properties of the chosen price variant as first level properties for the protected data of the transaction
 *
 * @example
 * const priceVariant = {
 *   name: 'something',
 * }
 *
 * will be returned as:
 * const priceVariant = {
 *   priceVariantName: 'something',
 * }
 *
 * @param {Object} priceVariant - The price variant object
 * @returns {Object} The price variant object with the properties prefixed with priceVariant*
 */
const prefixPriceVariantProperties = priceVariant => {
  if (!priceVariant) {
    return {};
  }

  const entries = Object.entries(priceVariant).map(([key, value]) => {
    return [`priceVariant${capitalizeString(key)}`, value];
  });
  return Object.fromEntries(entries);
};

/**
 * Construct orderParams object using pageData from session storage, shipping details, and optional payment params.
 * Note: This is used for both speculate transition and real transition
 *       - Speculate transition is called, when the the component is mounted. It's used to test if the data can go through the API validation
 *       - Real transition is made, when the user submits the StripePaymentForm.
 *
 * @param {Object} pageData data that's saved to session storage.
 * @param {Object} shippingDetails shipping address if applicable.
 * @param {Object} optionalPaymentParams (E.g. paymentMethod or setupPaymentMethodForSaving)
 * @param {Object} config app-wide configs. This contains hosted configs too.
 * @returns orderParams.
 */
export const getOrderParams = (pageData, shippingDetails, optionalPaymentParams, config) => {
  const quantity = pageData.orderData?.quantity;
  const quantityMaybe = quantity ? { quantity } : {};
  const seats = pageData.orderData?.seats;
  const seatsMaybe = seats ? { seats } : {};
  const deliveryMethod = pageData.orderData?.deliveryMethod;
  const deliveryMethodMaybe = deliveryMethod ? { deliveryMethod } : {};
  const { listingType, unitType, priceVariants } = pageData?.listing?.attributes?.publicData || {};

  // price variant data for fixed duration bookings
  const priceVariantName = pageData.orderData?.priceVariantName;
  const priceVariantNameMaybe = priceVariantName ? { priceVariantName } : {};
  const priceVariant = priceVariants?.find(pv => pv.name === priceVariantName);
  const priceVariantMaybe = priceVariant ? prefixPriceVariantProperties(priceVariant) : {};

  const protectedDataMaybe = {
    protectedData: {
      lockerCode: pageData.orderData?.lockerCode,
      ...getTransactionTypeData(listingType, unitType, config),
      ...deliveryMethodMaybe,
      ...shippingDetails,
      ...priceVariantMaybe,
    },
  };

  // Note: Avoid misinterpreting the following logic as allowing arbitrary mixing of `quantity` and `seats`.
  // You can only pass either quantity OR seats and units to the orderParams object
  // Quantity represents the total booked units for the line item (e.g. days, hours).
  // When quantity is not passed, we pass seats and units.
  // If `bookingDatesMaybe` is provided, it determines `units`, and `seats` defaults to 1
  // (implying quantity = units)

  // These are the order parameters for the first payment-related transition
  // which is either initiate-transition or initiate-transition-after-enquiry
  const orderParams = {
    listingId: pageData?.listing?.id,
    ...deliveryMethodMaybe,
    ...quantityMaybe,
    ...seatsMaybe,
    ...bookingDatesMaybe(pageData.orderData?.bookingDates),
    ...priceVariantNameMaybe,
    ...protectedDataMaybe,
    ...optionalPaymentParams,
    shippingCharge: pageData.orderData?.shippingCharge,
  };
  return orderParams;
};

const fetchSpeculatedTransactionIfNeeded = (orderParams, pageData, fetchSpeculatedTransaction) => {
  const tx = pageData ? pageData.transaction : null;
  const pageDataListing = pageData.listing;
  const processName =
    tx?.attributes?.processName ||
    pageDataListing?.attributes?.publicData?.transactionProcessAlias?.split('/')[0];
  const process = processName ? getProcess(processName) : null;

  // If transaction has passed payment-pending state, speculated tx is not needed.
  const shouldFetchSpeculatedTransaction =
    !!pageData?.listing?.id &&
    !!pageData.orderData &&
    !!process &&
    !hasTransactionPassedPendingPayment(tx, process);

  if (shouldFetchSpeculatedTransaction) {
    const processAlias = pageData.listing.attributes.publicData?.transactionProcessAlias;
    const transactionId = tx ? tx.id : null;
    const isInquiryInPaymentProcess =
      tx?.attributes?.lastTransition === process.transitions.INQUIRE;

    const requestTransition = isInquiryInPaymentProcess
      ? process.transitions.REQUEST_PAYMENT_AFTER_INQUIRY
      : process.transitions.REQUEST_PAYMENT;
    const isPrivileged = process.isPrivileged(requestTransition);

    fetchSpeculatedTransaction(
      orderParams,
      processAlias,
      transactionId,
      requestTransition,
      isPrivileged
    );
  }
};

/**
 * Load initial data for the page
 *
 * Since the data for the checkout is not passed in the URL (there
 * might be lots of options in the future), we must pass in the data
 * some other way. Currently the ListingPage sets the initial data
 * for the CheckoutPage's Redux store.
 *
 * For some cases (e.g. a refresh in the CheckoutPage), the Redux
 * store is empty. To handle that case, we store the received data
 * to window.sessionStorage and read it from there if no props from
 * the store exist.
 *
 * This function also sets of fetching the speculative transaction
 * based on this initial data.
 */
export const loadInitialDataForStripePayments = ({
  pageData,
  fetchSpeculatedTransaction,
  fetchStripeCustomer,
  config,
}) => {
  // console.log(pageData);
  // // Fetch currentUser with stripeCustomer entity
  // // Note: since there's need for data loading in "componentWillMount" function,
  // //       this is added here instead of loadData static function.
  // fetchStripeCustomer();

  // Fetch speculated transaction for showing price in order breakdown
  // NOTE: if unit type is line-item/item, quantity needs to be added.
  // The way to pass it to checkout page is through pageData.orderData
  const shippingDetails = {};
  const optionalPaymentParams = {};
  const orderParams = getOrderParams(pageData, shippingDetails, optionalPaymentParams, config);

  fetchSpeculatedTransactionIfNeeded(orderParams, pageData, fetchSpeculatedTransaction);
};

// const handleSubmit = (values, process, props, stripe, submitting, setSubmitting) => {
//   if (submitting) {
//     return;
//   }
//   setSubmitting(true);

//   const {
//     history,
//     config,
//     routeConfiguration,
//     speculatedTransaction,
//     currentUser,
//     stripeCustomerFetched,
//     paymentIntent,
//     dispatch,
//     onInitiateOrder,
//     onConfirmCardPayment,
//     onConfirmPayment,
//     onSendMessage,
//     onSavePaymentMethod,
//     onSubmitCallback,
//     pageData,
//     setPageData,
//     sessionStorageKey,
//   } = props;
//   const { card, message, paymentMethod: selectedPaymentMethod, formValues } = values;
//   const { saveAfterOnetimePayment: saveAfterOnetimePaymentRaw } = formValues;

//   const saveAfterOnetimePayment =
//     Array.isArray(saveAfterOnetimePaymentRaw) && saveAfterOnetimePaymentRaw.length > 0;
//   const selectedPaymentFlow = paymentFlow(selectedPaymentMethod, saveAfterOnetimePayment);
//   const hasDefaultPaymentMethodSaved = hasDefaultPaymentMethod(stripeCustomerFetched, currentUser);
//   const stripePaymentMethodId = hasDefaultPaymentMethodSaved
//     ? currentUser?.stripeCustomer?.defaultPaymentMethod?.attributes?.stripePaymentMethodId
//     : null;

//   // If paymentIntent status is not waiting user action,
//   // confirmCardPayment has been called previously.
//   const hasPaymentIntentUserActionsDone =
//     paymentIntent && STRIPE_PI_USER_ACTIONS_DONE_STATUSES.includes(paymentIntent.status);

//   const requestPaymentParams = {
//     pageData,
//     speculatedTransaction,
//     stripe,
//     card,
//     billingDetails: getBillingDetails(formValues, currentUser),
//     message,
//     paymentIntent,
//     hasPaymentIntentUserActionsDone,
//     stripePaymentMethodId,
//     process,
//     onInitiateOrder,
//     onConfirmCardPayment,
//     onConfirmPayment,
//     onSendMessage,
//     onSavePaymentMethod,
//     sessionStorageKey,
//     stripeCustomer: currentUser?.stripeCustomer,
//     isPaymentFlowUseSavedCard: selectedPaymentFlow === USE_SAVED_CARD,
//     isPaymentFlowPayAndSaveCard: selectedPaymentFlow === PAY_AND_SAVE_FOR_LATER_USE,
//     setPageData,
//   };

//   const shippingDetails = getShippingDetailsMaybe(formValues);
//   // Note: optionalPaymentParams contains Stripe paymentMethod,
//   // but that can also be passed on Step 2
//   // stripe.confirmCardPayment(stripe, { payment_method: stripePaymentMethodId })
//   const optionalPaymentParams =
//     selectedPaymentFlow === USE_SAVED_CARD && hasDefaultPaymentMethodSaved
//       ? { paymentMethod: stripePaymentMethodId }
//       : selectedPaymentFlow === PAY_AND_SAVE_FOR_LATER_USE
//       ? { setupPaymentMethodForSaving: true }
//       : {};

//   // These are the order parameters for the first payment-related transition
//   // which is either initiate-transition or initiate-transition-after-enquiry
//   const orderParams = getOrderParams(pageData, shippingDetails, optionalPaymentParams, config);

//   // There are multiple XHR calls that needs to be made against Stripe API and Sharetribe Marketplace API on checkout with payments
//   processCheckoutWithPayment(orderParams, requestPaymentParams)
//     .then(response => {
//       const { orderId, messageSuccess, paymentMethodSaved } = response;
//       setSubmitting(false);

//       const initialMessageFailedToTransaction = messageSuccess ? null : orderId;
//       const orderDetailsPath = pathByRouteName('OrderDetailsPage', routeConfiguration, {
//         id: orderId.uuid,
//       });
//       const initialValues = {
//         initialMessageFailedToTransaction,
//         savePaymentMethodFailed: !paymentMethodSaved,
//       };

//       setOrderPageInitialValues(initialValues, routeConfiguration, dispatch);
//       onSubmitCallback();
//       history.push(orderDetailsPath);
//     })
//     .catch(err => {
//       console.error(err);
//       setSubmitting(false);
//     });
// };

// const handleSubmit = (process, props, submitting, setSubmitting) => {
const handleSubmit = (values, process, props, stripe, submitting, setSubmitting, loggedInUser) => {
  if (submitting) {
    return;
  }
  if (values.formValues.building) {
    values.formValues.location.selectedPlace.building = values.formValues.building;
  }
  const selectedPlace = values.formValues.location.selectedPlace;
  // console.log(selectedPlace, values.formValues);
  // console.log(x);
  setSubmitting(true);

  const {
    history,
    config,
    routeConfiguration,
    speculatedTransaction,
    currentUser,

    dispatch,
    onInitiateOrder,
    onConfirmCardPayment,
    onConfirmPayment,
    onSendMessage,
    onSubmitCallback,
    pageData,
    setPageData,
    sessionStorageKey,
  } = props;
  // const { message } = values;
  const { card, message, formValues } = values;
  // console.log(777, values);

  // If paymentIntent status is not waiting user action,
  // confirmCardPayment has been called previously.

  const requestPaymentParams = {
    pageData,
    speculatedTransaction,
    billingDetails: getBillingDetails(formValues, currentUser),
    message,
    process,
    onInitiateOrder,
    onConfirmCardPayment,
    onConfirmPayment,
    onSendMessage,
    sessionStorageKey,
    setPageData,
  };

  // const shippingDetails = getShippingDetailsMaybe(formValues);
  const shippingDetails = null;
  // Note: optionalPaymentParams contains Stripe paymentMethod,
  // but that can also be passed on Step 2
  // stripe.confirmCardPayment(stripe, { payment_method: stripePaymentMethodId })
  const optionalPaymentParams = {};

  // These are the order parameters for the first payment-related transition
  // which is either initiate-transition or initiate-transition-after-enquiry
  const orderParams = getOrderParams(pageData, shippingDetails, optionalPaymentParams, config);
  const userInfo = {
    ...(formValues.recipientName ? { name: formValues.recipientName } : {}),
    ...(formValues.recipientEmail ? { email: formValues.recipientEmail } : {}),
    ...(formValues.recipientPhoneNumber ? { phoneNumber: formValues.recipientPhoneNumber } : {}),
  };
  const guestUserDetails =
    loggedInUser.type === loggedInUserType.guest ? { guestUserDetails: userInfo } : {};
  const updatedOrderParams = { ...orderParams, ...guestUserDetails };
  processCustomCheckout(updatedOrderParams, requestPaymentParams)
    .then(response => {
      if (response) {
        const { orderId, messageSuccess } = response;
        setSubmitting(false);

        const initialMessageFailedToTransaction = messageSuccess ? null : orderId;
        const orderDetailsPath = pathByRouteName('OrderDetailsPage', routeConfiguration, {
          id: orderId.uuid,
        });
        const initialValues = {
          initialMessageFailedToTransaction,
        };

        setOrderPageInitialValues(initialValues, routeConfiguration, dispatch);
      }
      onSubmitCallback();
      // history.push(orderDetailsPath);
    })
    .catch(err => {
      console.log(err);
      setSubmitting(false);
    });

  // There are multiple XHR calls that needs to be made against Stripe API and Sharetribe Marketplace API on checkout with payments
  // processCheckoutWithPayment(orderParams, requestPaymentParams)
  //   .then(response => {
  //     const { orderId, messageSuccess } = response;
  //     setSubmitting(false);

  //     const initialMessageFailedToTransaction = messageSuccess ? null : orderId;
  //     const orderDetailsPath = pathByRouteName('OrderDetailsPage', routeConfiguration, {
  //       id: orderId.uuid,
  //     });
  //     const initialValues = {
  //       initialMessageFailedToTransaction,
  //     };

  //     setOrderPageInitialValues(initialValues, routeConfiguration, dispatch);
  //     onSubmitCallback();
  //     history.push(orderDetailsPath);
  //   })
  //   .catch(err => {
  //     console.error(err);
  //     setSubmitting(false);
  //   });
};

const onStripeInitialized = (stripe, process, props) => {
  const { paymentIntent, onRetrievePaymentIntent, pageData } = props;
  const tx = pageData?.transaction || null;

  // We need to get up to date PI, if payment is pending but it's not expired.
  const shouldFetchPaymentIntent =
    stripe &&
    !paymentIntent &&
    tx?.id &&
    process?.getState(tx) === process?.states.PENDING_PAYMENT &&
    !hasPaymentExpired(tx, process);

  if (shouldFetchPaymentIntent) {
    const { stripePaymentIntentClientSecret } =
      tx.attributes.protectedData?.stripePaymentIntents?.default || {};

    // Fetch up to date PaymentIntent from Stripe
    onRetrievePaymentIntent({ stripe, stripePaymentIntentClientSecret });
  }
};

const prepareInitialValue = (loggedInUser, orderData, pageData) => {
  if (
    typeof pageData.formValues === 'object' &&
    pageData.formValues !== null &&
    pageData.formValues.constructor === Object
  ) {
    return { ...pageData.formValues };
  }
  // Get first and last name of the current user and use it in the StripePaymentForm to autofill the name field
  const userName = loggedInUser?.attributes?.profile
    ? `${loggedInUser.attributes.profile.firstName ?? ''} ${loggedInUser.attributes.profile
        .lastName ?? ''}`.trim()
    : null;
  const phoneNumber = loggedInUser?.attributes?.profile?.protectedData?.phoneNumber || '';
  const email = loggedInUser?.attributes?.email || '';
  const { deliveryDoor = [], deliveryDoorDefault } =
    loggedInUser?.attributes.profile.privateData || {};
  const defaultDoorAddress = deliveryDoor.find(
    p => p.id === deliveryDoorDefault && deliveryDoorDefault
  );
  const origin = defaultDoorAddress
    ? defaultDoorAddress.origin || defaultDoorAddress.location?.origin
    : null;
  const { address: _a, origin: _o, bounds: _b, ...restPredictionValues } =
    defaultDoorAddress?.selectedPlace || {};
  const doorLocation = defaultDoorAddress
    ? {
        predictions: [],
        search: defaultDoorAddress.address,
        selectedPlace: {
          address: defaultDoorAddress.address,
          origin: origin && origin.lat && origin.lng ? new LatLng(origin.lat, origin.lng) : null,
          ...restPredictionValues,
        },
      }
    : null;

  const shippingType = orderData?.shippingType || '';
  const location = orderData?.location || doorLocation || {};
  const initialValuesForStripePayment = {
    name: userName,
    recipientName: userName,
    recipientEmail: email,
    tempEmail: email,
    recipientPhoneNumber: phoneNumber,
    // shippingType: 'door',
    // building: defaultDoorAddress?.building || '',
    // addressType: orderData?.addressType || 'residential',
    // location: doorLocation || orderData?.location || {},
    shippingType,
    building:
      shippingType === 'door'
        ? defaultDoorAddress?.building || orderData?.building || ''
        : orderData?.building || '',
    addressType: orderData?.addressType || 'residential',
    location,
  };
  return initialValuesForStripePayment;
};

/**
 * A component that renders the checkout page with payment.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled - Whether the page should scroll
 * @param {string} props.speculateTransactionError - The error message for the speculate transaction
 * @param {propTypes.transaction} props.speculatedTransaction - The speculated transaction
 * @param {boolean} props.isClockInSync - Whether the clock is in sync
 * @param {string} props.initiateOrderError - The error message for the initiate order
 * @param {string} props.confirmPaymentError - The error message for the confirm payment
 * @param {intlShape} props.intl - The intl object
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {string} props.confirmCardPaymentError - The error message for the confirm card payment
 * @param {propTypes.paymentIntent} props.paymentIntent - The Stripe's payment intent
 * @param {boolean} props.stripeCustomerFetched - Whether the stripe customer has been fetched
 * @param {Object} props.pageData - The page data
 * @param {propTypes.listing} props.pageData.listing - The listing entity
 * @param {propTypes.transaction} props.pageData.transaction - The transaction entity
 * @param {Object} props.pageData.orderData - The order data
 * @param {string} props.processName - The process name
 * @param {string} props.listingTitle - The listing title
 * @param {string} props.title - The title
 * @param {Function} props.onInitiateOrder - The function to initiate the order
 * @param {Function} props.onConfirmCardPayment - The function to confirm the card payment
 * @param {Function} props.onConfirmPayment - The function to confirm the payment after Stripe call is made
 * @param {Function} props.onSendMessage - The function to send a message
 * @param {Function} props.onSavePaymentMethod - The function to save the payment method for later use
 * @param {Function} props.onSubmitCallback - The function to submit the callback
 * @param {propTypes.error} props.initiateOrderError - The error message for the initiate order
 * @param {propTypes.error} props.confirmPaymentError - The error message for the confirm payment
 * @param {propTypes.error} props.confirmCardPaymentError - The error message for the confirm card payment
 * @param {propTypes.paymentIntent} props.paymentIntent - The Stripe's payment intent
 * @param {boolean} props.stripeCustomerFetched - Whether the stripe customer has been fetched
 * @param {Object} props.config - The config
 * @param {Object} props.routeConfiguration - The route configuration
 * @param {Object} props.history - The history object
 * @param {Object} props.history.push - The push state function of the history object
 * @returns {JSX.Element}
 */
export const CheckoutPageWithPayment = props => {
  const [submitting, setSubmitting] = useState(false);
  // Initialized stripe library is saved to state - if it's needed at some point here too.
  const [stripe, setStripe] = useState(null);
  const [state, setState] = useState({
    errorInPayment: null,
    paymentStatus: null,
    shippingRateFetchInProgress: false,
    shippingRateFetchFailed: null,
    showGuestUserOrderSuccessModal: false,
    checkoutConfirmInProgress: false,
  });
  const {
    scrollingDisabled,
    speculateTransactionError,
    speculatedTransaction: speculatedTransactionMaybe,
    isClockInSync,
    initiateOrderError,
    confirmPaymentError,
    intl,
    confirmCardPaymentError,
    paymentIntent,
    retrievePaymentIntentError,
    stripeCustomerFetched,
    pageData,
    setPageData,
    processName,
    listingTitle,
    title,
    config,
    onConfirmPayment,
    sessionStorageKey,
    dispatch,
    routeConfiguration,
    onSubmitCallback,
    history,
    callSetInitialValues,
    loggedInUser,
    checkoutDetailFetchInProgress,
    checkoutDetailsFetchError,
    checkoutDetails,
    totalAvailableForCheckout,
    fetchInProgress,
    onLogoutUser,
    onManageDisableScrolling,
    onFetchCurrentUserCartCount,
  } = props;

  const loadingCount = useRef(0);

  const updateState = state => {
    setState(prevState => ({ ...prevState, ...state }));
  };

  const setShippingRateFetchStatus = (status, err) => {
    updateState({ shippingRateFetchInProgress: status, shippingRateFetchFailed: err ?? null });
  };

  const closeGuestUserSuccessModal = () => {
    updateState({ showGuestUserOrderSuccessModal: false });
  };

  const redirectToListingPage = () => {
    closeGuestUserSuccessModal();
    const params = { id: listing?.id?.uuid, slug: createSlug(listingTitle) };
    const orderDetailsPath = pathByRouteName('ListingPage', routeConfiguration, params);
    history.replace(orderDetailsPath);
  };

  const redirectToLogin = () => {
    closeGuestUserSuccessModal();
    const orderDetailsPath = pathByRouteName('LoginPage', routeConfiguration);
    history.replace(orderDetailsPath);
  };

  const loading =
    checkoutDetailFetchInProgress || fetchInProgress || state.shippingRateFetchInProgress;
  const orderBreakdownInProgress = fetchInProgress || state.shippingRateFetchInProgress;

  // Since the listing data is already given from the ListingPage
  // and stored to handle refreshes, it might not have the possible
  // deleted or closed information in it. If the transaction
  // initiate or the speculative initiate fail due to the listing
  // being deleted or closed, we should dig the information from the
  // errors and not the listing data.
  const listingNotFound =
    isTransactionInitiateListingNotFoundError(speculateTransactionError) ||
    isTransactionInitiateListingNotFoundError(initiateOrderError);

  const { listing, transaction, orderData } = pageData;
  const existingTransaction = ensureTransaction(transaction);
  const speculatedTransaction = ensureTransaction(speculatedTransactionMaybe, {}, null);

  // If existing transaction has line-items, it has gone through one of the request-payment transitions.
  // Otherwise, we try to rely on speculatedTransaction for order breakdown data.
  const tx =
    existingTransaction?.attributes?.lineItems?.length > 0
      ? existingTransaction
      : speculatedTransaction;
  const timeZone = listing?.attributes?.availabilityPlan?.timezone;
  const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias;
  // const shippingTypePresentInListing = listing?.attributes?.publicData?.shippingType;
  // const priceVariantName = tx.attributes.protectedData?.priceVariantName;

  const txBookingMaybe = tx?.booking?.id ? { booking: tx.booking, timeZone } : {};

  // Show breakdown only when (speculated?) transaction is loaded
  // (i.e. it has an id and lineItems)
  const breakdown =
    tx.id && tx.attributes.lineItems?.length > 0 && !orderBreakdownInProgress ? (
      <OrderBreakdown
        className={css.orderBreakdown}
        userRole="customer"
        transaction={tx}
        {...txBookingMaybe}
        currency={config.currency}
        marketplaceName={config.marketplaceName}
        hideItemPrice={true}
        heading={<FormattedMessage id="myOrder" />}
        totalPriceClassName={css.totalPriceContainer}
        totalDividerClassName={css.totalPriceDivider}
      />
    ) : loading ? (
      <div className={classNames(css.orderBreakdown, css.breakdownLoader)}>
        <IconSpinner className={css.loadingIcon} />
      </div>
    ) : null;

  const totalPrice =
    tx?.attributes?.lineItems?.length > 0 ? getFormattedTotalPrice(tx, intl) : null;

  const process = processName ? getProcess(processName) : null;
  const transitions = process.transitions;
  const isPaymentExpired = hasPaymentExpired(existingTransaction, process, isClockInSync);
  const unknownUser = loggedInUser.type === loggedInUserType.none;
  // Allow showing page when currentUser is still being downloaded,
  // but show payment form only when user info is loaded.
  const showPaymentForm = !!(
    !unknownUser &&
    !listingNotFound &&
    !initiateOrderError &&
    !speculateTransactionError &&
    !retrievePaymentIntentError &&
    !isPaymentExpired
  );

  const listingLink = (
    <NamedLink
      name="ListingPage"
      params={{ id: listing?.id?.uuid, slug: createSlug(listingTitle) }}
    >
      <FormattedMessage id="CheckoutPage.errorlistingLinkText" />
    </NamedLink>
  );

  const errorMessages = getErrorMessages(
    listingNotFound,
    initiateOrderError,
    isPaymentExpired,
    retrievePaymentIntentError,
    speculateTransactionError,
    listingLink,
    state.shippingRateFetchFailed,
    checkoutDetailsFetchError
  );

  const txTransitions = existingTransaction?.attributes?.transitions || [];
  const hasInquireTransition = txTransitions.find(tr => tr.transition === transitions.INQUIRE);
  const showInitialMessageInput = !hasInquireTransition && checkoutDetails.length === 1;

  // If paymentIntent status is not waiting user action,
  // confirmCardPayment has been called previously.
  const hasPaymentIntentUserActionsDone =
    paymentIntent && STRIPE_PI_USER_ACTIONS_DONE_STATUSES.includes(paymentIntent.status);

  // If your marketplace works mostly in one country you can use initial values to select country automatically
  // e.g. {country: 'FI'}

  const initialValuesForStripePayment = prepareInitialValue(loggedInUser, orderData, pageData);

  // console.log(orderData);

  const askShippingDetails =
    orderData?.deliveryMethod === 'shipping' &&
    !hasTransactionPassedPendingPayment(existingTransaction, process);

  // Check if the listing currency is compatible with Stripe for the specified transaction process.
  // This function validates the currency against the transaction process requirements and
  // ensures it is supported by Stripe, as indicated by the 'stripe' parameter.
  // If using a transaction process without any stripe actions, leave out the 'stripe' parameter.
  const isStripeCompatibleCurrency = true;

  // const isStripeCompatibleCurrency = isValidCurrencyForTransactionProcess(
  //   transactionProcessAlias,
  //   listing.attributes.price.currency,
  //   'stripe'
  // );

  // Render an error message if the listing is using a non Stripe supported currency
  // and is using a transaction process with Stripe actions (default-booking or default-purchase)
  // if (!isStripeCompatibleCurrency && false) {
  //   return (
  //     <Page title={title} scrollingDisabled={scrollingDisabled}>
  //       <CustomTopbar intl={intl} linkToExternalSite={config?.topbar?.logoLink} />
  //       <div className={css.contentContainer}>
  //         <section className={css.incompatibleCurrency}>
  //           <H4 as="h1" className={css.heading}>
  //             <FormattedMessage id="CheckoutPage.incompatibleCurrency" />
  //           </H4>
  //         </section>
  //       </div>
  //     </Page>
  //   );
  // }

  // Payment Error Component
  const PaymentErrorComponent = ({ error, onRetry }) => (
    <div className={css.paymentErrorContainer}>
      <div className={css.paymentErrorContent}>
        <div className={css.paymentErrorIcon}>
          <IconAlert />
        </div>
        <div className={css.paymentErrorText}>
          <H4 className={css.paymentErrorTitle}>
            <FormattedMessage id="CheckoutPage.paymentErrorTitle" />
          </H4>
          <p className={css.paymentErrorMessage}>
            <FormattedMessage id="CheckoutPage.paymentErrorMessage" />
          </p>
          {error && (
            // <details className={css.paymentErrorDetails}>
            //   <summary className={css.paymentErrorSummary}>
            //     <FormattedMessage id="CheckoutPage.paymentErrorDetails" values={{ error: '' }} />
            //   </summary>
            <div className={css.paymentErrorDetailsContent}>{error}</div>
            // </details>
          )}
          <p className={css.paymentContactSupport}>
            <FormattedMessage id="CheckoutPage.paymentContactSupport" />
          </p>
        </div>
        <div className={css.paymentErrorActions}>
          <Button className={css.paymentRetryButton} onClick={onRetry} type="button">
            {state.paymentStatus === 'timeout' ? (
              <FormattedMessage id="CheckoutPage.goToProductPage" />
            ) : (
              <FormattedMessage id="CheckoutPage.paymentRetryButton" />
            )}
          </Button>
          {/* <Button
            className={css.paymentRefreshButton}
            onClick={() => window.location.reload()}
            type="button"
            inProgress={false}
            disabled={false}
          >
            <FormattedMessage id="CheckoutPage.paymentRefreshButton" />
          </Button> */}
        </div>
      </div>
    </div>
  );

  const handlePaymentRetry = () => {
    updateState({ errorInPayment: null });
    // The iframe will automatically reload when errorInPayment is cleared
    if (state.paymentStatus === 'timeout') {
      history.push(
        createResourceLocatorString(
          'ListingPage',
          routeConfiguration,
          { slug: createSlug(listingTitle), id: listing?.id?.uuid },
          {}
        )
      );
    }
  };

  const shouldShowInitialLoader =
    state.checkoutConfirmInProgress || (loading && loadingCount.current < 2);

  useEffect(() => {
    const listener = event => {
      if (event.origin !== listenerOrigin) return;
      updateState({ paymentStatus: event.data.tradesafePaymentResponse.status });

      if (event.data.tradesafePaymentResponse.status !== 'success') {
        updateState({ errorInPayment: event.data.tradesafePaymentResponse.message });
      }

      if (event.data.tradesafePaymentResponse.status === 'success') {
        setState({ checkoutConfirmInProgress: true });
        const requestPaymentParams = {
          pageData,
          process,
          setPageData,
          onConfirmPayment,
          sessionStorageKey,
        };

        const orderParams = Array.isArray(pageData?.transactions)
          ? pageData.transactions.map(tx => ({
              id: tx.id,
              tradesafeTxId: tx.attributes.protectedData.tradesafeTxId,
            }))
          : pageData?.transaction
          ? [
              {
                id: pageData.transaction.id,
                tradesafeTxId: pageData.transaction.attributes.protectedData.tradesafeTxId,
              },
            ]
          : [];

        const orderParamsPromise = orderParams.map(async o => {
          const tradesafeTxId = o.tradesafeTxId;
          const url = `${apiBaseUrl()}/api/tradesafe/queryTransaction/${tradesafeTxId}`;
          const resp = await axios.get(`${url}`, { withCredentials: true });
          const state = resp?.data?.data?.state;
          return;
          // if (state !== 'FUNDS_RECEIVED') {
          //   return;
          // }
          // return processCheckoutWithCustomPayment(o, requestPaymentParams);
        });
        const isGuestUser = loggedInUser.type === loggedInUserType.guest;
        let orders = [];
        Promise.all(orderParamsPromise)
          .then(resp => {
            orders = resp;
            // if (isGuestUser) {
            //   return onLogoutUser();
            // }
            return;
          })
          .then(() => {
            const transactionDbId = orders[0]?.attributes?.protectedData?.transactionDbId;
            if (transactionDbId) {
              const url = `${apiBaseUrl()}/api/booking/checkout-complete`;
              return axios.post(url, { transactionDbId }, { withCredentials: true });
            }
            return;
          })
          .then(_ => {
            onFetchCurrentUserCartCount();

            // Track purchase with Google Analytics 4 (GA4) using official ecommerce schema
            if (typeof window !== 'undefined' && window.gtag) {
              try {
                const transactions = Array.isArray(pageData?.transactions)
                  ? pageData.transactions
                  : pageData?.transaction
                  ? [pageData.transaction]
                  : [];

                transactions.forEach((transaction, index) => {
                  const txAttributes = transaction?.attributes;
                  const payinTotal = txAttributes?.payinTotal;
                  const lineItems = txAttributes?.lineItems || [];

                  // Extract transaction details
                  const transactionId = transaction?.id?.uuid;
                  const currency = payinTotal?.currency || 'ZAR';
                  const totalValue = payinTotal?.amount ? payinTotal.amount / 100 : 0;

                  // Get listing information
                  const listingId =
                    transaction?.relationships?.listing?.data?.id?.uuid || 'unknown';
                  const listingTitle = txAttributes?.protectedData?.listingTitle || 'Product';
                  const quantity = txAttributes?.protectedData?.quantity || 1;

                  // Calculate shipping and tax from line items
                  let shipping = 0;
                  let tax = 0;
                  let itemPrice = 0;

                  lineItems.forEach(item => {
                    const itemCode = item.code;
                    const itemAmount = item.unitPrice?.amount ? item.unitPrice.amount / 100 : 0;

                    if (itemCode === 'line-item/shipping-charge') {
                      shipping += itemAmount;
                    } else if (itemCode === 'line-item/provider-commission') {
                      // Treat commission as tax if applicable
                      tax += itemAmount;
                    } else if (itemCode === 'line-item/units' || itemCode === 'line-item/item') {
                      itemPrice = itemAmount;
                    }
                  });

                  // Send purchase event to Google Analytics with official GA4 ecommerce schema
                  window.gtag('event', 'purchase', {
                    transaction_id: transactionId,
                    value: totalValue,
                    currency: currency,
                    tax: tax,
                    shipping: shipping,
                    items: [
                      {
                        item_id: listingId,
                        item_name: listingTitle,
                        price: itemPrice || totalValue - shipping - tax,
                        currency: currency,
                        quantity: quantity,
                      },
                    ],
                  });
                });
              } catch (error) {
                console.error('Failed to track purchase with Google Analytics:', error);
              }
            }

            // Track purchase with Meta Pixel
            if (typeof window !== 'undefined' && window.fbq) {
              try {
                const transactions = Array.isArray(pageData?.transactions)
                  ? pageData.transactions
                  : pageData?.transaction
                  ? [pageData.transaction]
                  : [];

                const totalValue = transactions.reduce((sum, tx) => {
                  const amount = tx?.attributes?.payinTotal?.amount || 0;
                  return sum + amount / 100;
                }, 0);

                const currency = transactions[0]?.attributes?.payinTotal?.currency || 'ZAR';

                window.fbq('track', 'Purchase', {
                  value: totalValue,
                  currency: currency,
                });
              } catch (error) {
                console.error('Failed to track purchase with Meta Pixel:', error);
              }
            }

            // if (isGuestUser) {
            //   onSubmitCallback();
            //   updateState({
            //     showGuestUserOrderSuccessModal: true,
            //   });
            //   return;
            // }
            const isSingleOrder = orderParams.length === 1;
            const pageName = isSingleOrder ? 'OrderDetailsPage' : 'InboxPage';
            const pathParams = isSingleOrder ? { id: orderParams[0].id.uuid } : { tab: 'orders' };
            const orderDetailsPath = pathByRouteName(pageName, routeConfiguration, pathParams);

            setOrderPageInitialValues({}, routeConfiguration, dispatch);
            onSubmitCallback();
            setState({ checkoutConfirmInProgress: false });
            history.replace(orderDetailsPath);
          })
          .catch(err => {
            console.error(err);
            setSubmitting(false);
          });
      }
    };

    typeof window !== 'undefined' && window.addEventListener('message', listener);
    return () => typeof window !== 'undefined' && window.removeEventListener('message', listener);
  }, [pageData]);

  useEffect(() => {
    if (loading || (loadingCount.current > 0 && !loading)) {
      loadingCount.current += 1;
    }
  }, [loading]);

  // Track begin_checkout event with Google Analytics 4
  useEffect(() => {
    if (
      checkoutDetails &&
      checkoutDetails.length > 0 &&
      !checkoutDetailFetchInProgress &&
      typeof window !== 'undefined' &&
      window.gtag
    ) {
      try {
        const items = checkoutDetails.map(item => {
          const listingPrice = item.listing?.attributes?.price;
          const priceValue = listingPrice?.amount ? listingPrice.amount / 100 : 0;
          const currency = listingPrice?.currency || 'ZAR';
          const quantity = item.qty || 1;

          return {
            item_id: item.listing?.id?.uuid,
            item_name: item.listing?.attributes?.title || 'Product',
            price: priceValue,
            currency: currency,
            quantity: quantity,
          };
        });

        const totalValue = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
        const currency = items[0]?.currency || 'ZAR';

        window.gtag('event', 'begin_checkout', {
          currency: currency,
          value: totalValue,
          items: items,
        });
      } catch (error) {
        console.error('Failed to track begin_checkout with Google Analytics:', error);
      }
    }
  }, [checkoutDetails, checkoutDetailFetchInProgress]);

  return (
    <Page
      title={title}
      scrollingDisabled={scrollingDisabled}
      className={css.pageRoot}
      contentClassName={css.pageContent}
    >
      <CustomTopbar intl={intl} linkToExternalSite={config?.topbar?.logoLink} />
      {shouldShowInitialLoader ? (
        <div className={css.loaderContainer}>
          {/* <IconSpinner className={css.loader} /> */}
          <Loader />
        </div>
      ) : (
        <React.Fragment>
          <div className={css.contentContainer}>
            {pageData?.paymentLink && state.errorInPayment ? (
              <PaymentErrorComponent error={state.errorInPayment} onRetry={handlePaymentRetry} />
            ) : pageData?.paymentLink && !state.errorInPayment ? (
              <iframe src={pageData.paymentLink} width="100%" height="750" frameBorder="0" />
            ) : (
              <>
                {/* <MobileListingImage
             listingTitle={listingTitle}
             author={listing?.author}
             firstImage={firstImage}
             layoutListingImageConfig={config.layout.listingImage}
           /> */}
                <div className={css.orderFormContainer}>
                  <div className={css.headingContainer}>
                    <H3 as="h1" className={css.heading}>
                      <div className={css.backBtnContainer}>
                        <BackButton />
                      </div>
                      {title}
                    </H3>
                    <CustomSlideCard
                      listings={checkoutDetails}
                      intl={intl}
                      config={config}
                      sliderRootClassName={css.mobileCardDetails}
                    />
                  </div>

                  {/* <MobileOrderBreakdown
                    speculateTransactionErrorMessage={
                      errorMessages.speculateTransactionErrorMessage
                    }
                    breakdown={breakdown}
                    priceVariantName={priceVariantName}
                  /> */}
                  <section className={css.paymentContainer}>
                    {errorMessages.initiateOrderErrorMessage}
                    {errorMessages.listingNotFoundErrorMessage}
                    {errorMessages.speculateErrorMessage}
                    {errorMessages.retrievePaymentIntentErrorMessage}
                    {errorMessages.paymentExpiredMessage}
                    {/* {errorMessages.shippingRateFetchErrorMessage} */}
                    {errorMessages.checkoutDetailsFetchErrorMessage}

                    {showPaymentForm ? (
                      <PaymentForm
                        className={css.paymentForm}
                        onSubmit={values =>
                          handleSubmit(
                            values,
                            process,
                            props,
                            stripe,
                            submitting,
                            setSubmitting,
                            loggedInUser
                          )
                        }
                        inProgress={submitting}
                        formId="CheckoutPagePaymentForm"
                        authorDisplayName={listing?.author?.attributes?.profile?.displayName}
                        showInitialMessageInput={showInitialMessageInput}
                        initialValues={initialValuesForStripePayment}
                        initiateOrderError={initiateOrderError}
                        shippingRateFetchErrorMessage={errorMessages.shippingRateFetchErrorMessage}
                        confirmCardPaymentError={confirmCardPaymentError}
                        confirmPaymentError={confirmPaymentError}
                        hasHandledCardPayment={hasPaymentIntentUserActionsDone}
                        // loadingData={!stripeCustomerFetched}
                        defaultPaymentMethod={
                          hasDefaultPaymentMethod(stripeCustomerFetched, loggedInUser)
                            ? loggedInUser.stripeCustomer.defaultPaymentMethod
                            : null
                        }
                        paymentIntent={paymentIntent}
                        onStripeInitialized={stripe => {
                          setStripe(stripe);
                          return onStripeInitialized(stripe, process, props);
                        }}
                        askShippingDetails={askShippingDetails}
                        showPickUplocation={orderData?.deliveryMethod === 'pickup'}
                        listingLocation={listing?.attributes?.publicData?.location}
                        building={listing?.attributes?.publicData?.building}
                        totalPrice={totalPrice}
                        locale={config.localization.locale}
                        stripePublishableKey={config.stripe.publishableKey}
                        marketplaceName={config.marketplaceName}
                        isBooking={isBookingProcessAlias(transactionProcessAlias)}
                        isFuzzyLocation={config.maps.fuzzy.enabled}
                        pageData={pageData}
                        setPageData={setPageData}
                        fetchSpeculatedTransaction={props.fetchSpeculatedTransaction}
                        callSetInitialValues={callSetInitialValues}
                        lockerData={config.lockerData}
                        dataLoadInProgress={loading}
                        setShippingRateFetchStatus={setShippingRateFetchStatus}
                        config={config}
                        hasOutOfStockListing={totalAvailableForCheckout !== checkoutDetails.length}
                      />
                    ) : null}

                    <div>
                      {/* <Button onClick={() => handleSubmit(process, props, submitting, setSubmitting)}>
               Book
             </Button> */}
                    </div>
                  </section>
                </div>
                <div className={css.leftContainer}>
                  <CustomSlideCard
                    listings={checkoutDetails}
                    intl={intl}
                    config={config}
                    sliderRootClassName={css.desktopCardDetails}
                  />
                  {breakdown}
                </div>

                {/* <DetailsSideCard
                  listing={listing}
                  listingTitle={listingTitle}
                  priceVariantName={priceVariantName}
                  author={listing?.author}
                  firstImage={firstImage}
                  layoutListingImageConfig={config.layout.listingImage}
                  speculateTransactionErrorMessage={errorMessages.speculateTransactionErrorMessage}
                  isInquiryProcess={false}
                  processName={processName}
                  breakdown={breakdown}
                  intl={intl}
                /> */}
              </>
            )}
          </div>
        </React.Fragment>
      )}
      <Modal
        id="checkoutPage-guestuser"
        onClose={redirectToListingPage}
        isOpen={state.showGuestUserOrderSuccessModal}
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <H3 as="h3" className={css.heading}>
          <FormattedMessage id="GuestUser.checkoutComplt.title" />
        </H3>
        <p className={css.checkoutCompltBody}>
          <FormattedMessage
            id="GuestUser.checkoutComplt.body"
            values={{ email: pageData.formValues?.recipientEmail || '' }}
          />
        </p>
        <div className={css.checkCompltActionContainer}>
          <SecondaryButton onClick={redirectToListingPage}>
            <FormattedMessage id="GuestUser.checkoutComplt.cancelBtn" />
          </SecondaryButton>
          <PrimaryButton onClick={redirectToLogin}>
            <FormattedMessage id="GuestUser.checkoutComplt.okBtn" />
          </PrimaryButton>
        </div>
      </Modal>
    </Page>
  );
};

export default CheckoutPageWithPayment;
